import React, { useState, useEffect, useMemo } from 'react';
import './Analytics.css';
import useAnalytics from '../hooks/useAnalytics';
import AdminLoading from './AdminLoading';
import { extractCreatorId, getAuthHeaders } from '../utils/authUtils';
import { getApiUrl } from '../../config/environment';
import { analyticsAdminService } from '../../services/api';
import { fetchAllAdminOrdersForAgg } from '../utils/adminOrdersBulkFetch';
import {
  aggregateOrderStatsFromOrders,
  filterOrdersForAnalyticsScope,
  pickAnalyticsStatusCounts,
} from '../utils/orderAnalyticsAgg';
import {
  allCreatorsMatcher,
  buildSalesReport,
  filterOrdersByDate,
  teamMemberCreatorMatcher,
} from '../utils/teamSalesReport';

function formatPkrs(amount) {
  return `PKR ${(Number(amount) || 0).toLocaleString()}`;
}

function AnalyticsSectionHead({ icon, title, description, id }) {
  return (
    <header className="analytics-section-head">
      <span className="analytics-section-head__icon" aria-hidden>
        <i className={`fas ${icon}`} />
      </span>
      <div className="analytics-section-head__text">
        <h2 id={id}>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
    </header>
  );
}

function AnalyticsMetricTile({ label, value, muted = false }) {
  return (
    <div className={`analytics-metric-tile${muted ? ' is-muted' : ''}`}>
      <span className="analytics-metric-tile__label">{label}</span>
      <span className="analytics-metric-tile__value">{value}</span>
    </div>
  );
}

function AnalyticsInfoCallout({ children }) {
  return (
    <div className="analytics-info-callout" role="note">
      <i className="fas fa-circle-info" aria-hidden />
      <p>{children}</p>
    </div>
  );
}

function AnalyticsPanel({
  variant = 'default',
  icon,
  title,
  hero,
  heroLabel,
  loading = false,
  children,
}) {
  return (
    <article className={`analytics-panel analytics-panel--${variant}`}>
      <header className="analytics-panel__header">
        <span className="analytics-panel__icon" aria-hidden>
          <i className={`fas ${icon}`} />
        </span>
        <h3>{title}</h3>
      </header>
      <div className="analytics-panel__body">
        {loading ? (
          <p className="analytics-panel__loading">Loading…</p>
        ) : (
          <>
            {hero != null && hero !== false ? (
              <div className="analytics-panel__hero">
                {heroLabel ? (
                  <span className="analytics-panel__hero-label">{heroLabel}</span>
                ) : null}
                <div className="analytics-panel__hero-value">{hero}</div>
              </div>
            ) : null}
            {children}
          </>
        )}
      </div>
    </article>
  );
}

const Analytics = ({ currentUser }) => {
  const isTeamMember = currentUser?.role === 'team';
  const { data: rawDashboardData, loading, error, refetch } = useAnalytics();

  // For team members: filter to only their products/orders (safety filter)
  const dashboardData = React.useMemo(() => {
    if (!rawDashboardData) return rawDashboardData;
    if (!isTeamMember) return rawDashboardData;
    const myId = String(currentUser?._id || currentUser?.id || '');
    if (!myId) return rawDashboardData;

    const isMyProduct = (product) => extractCreatorId(product?.createdBy) === myId;
    const isOrderWithMyProducts = (order) => {
      const items = order?.items || [];
      return items.some(item => {
        const product = item.product || item.productId;
        if (!product || typeof product !== 'object') return false;
        return extractCreatorId(product.createdBy) === myId;
      });
    };

    return {
      ...rawDashboardData,
      lowStockProducts: (rawDashboardData.lowStockProducts || []).filter(isMyProduct),
      recentOrders: (rawDashboardData.recentOrders || []).filter(isOrderWithMyProducts),
    };
  }, [rawDashboardData, isTeamMember, currentUser]);
  
  const [wishlistTotalLikes, setWishlistTotalLikes] = useState(null);
  const [productViewsTotal, setProductViewsTotal] = useState(null);
  const [productViewsTop, setProductViewsTop] = useState([]);
  const [soldSalesLoading, setSoldSalesLoading] = useState(true);
  const [soldOrders, setSoldOrders] = useState([]);

  const myId = String(currentUser?._id || currentUser?.id || '');
  const creatorMatcher = useMemo(
    () => (isTeamMember && myId ? teamMemberCreatorMatcher(myId) : allCreatorsMatcher()),
    [isTeamMember, myId]
  );

  const scopedOrdersForAnalytics = useMemo(
    () => filterOrdersForAnalyticsScope(soldOrders, { isTeamMember, myId }),
    [soldOrders, isTeamMember, myId]
  );

  const orderStatsFromApi = useMemo(
    () => aggregateOrderStatsFromOrders(scopedOrdersForAnalytics),
    [scopedOrdersForAnalytics]
  );

  const soldSales = useMemo(() => {
    const all = buildSalesReport(soldOrders, creatorMatcher);
    const today = buildSalesReport(filterOrdersByDate(soldOrders, 'today'), creatorMatcher);
    const week = buildSalesReport(filterOrdersByDate(soldOrders, '7d'), creatorMatcher);
    const month = buildSalesReport(filterOrdersByDate(soldOrders, 'month'), creatorMatcher);
    return { all, today, week, month };
  }, [soldOrders, creatorMatcher]);

  const loadSoldOrders = React.useCallback(async () => {
    setSoldSalesLoading(true);
    try {
      const list = await fetchAllAdminOrdersForAgg();
      setSoldOrders(list);
    } catch (e) {
      console.warn('Analytics sold payments:', e);
      setSoldOrders([]);
    } finally {
      setSoldSalesLoading(false);
    }
  }, []);

  const loadProductViewsSummary = React.useCallback(async () => {
    try {
      const res = await analyticsAdminService.getProductViewsSummary();
      if (res.success) {
        setProductViewsTotal(res.totalViews);
        setProductViewsTop(Array.isArray(res.topProducts) ? res.topProducts : []);
      } else {
        setProductViewsTotal(null);
        setProductViewsTop([]);
      }
    } catch {
      setProductViewsTotal(null);
      setProductViewsTop([]);
    }
  }, []);


  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(getApiUrl('/admin/wishlists/analytics'), { headers: getAuthHeaders() });
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const d = json?.data ?? json;
        const n =
          d.totalWishlistItems ??
          d.totalItems ??
          d.totalLikes ??
          d.totalSaves ??
          d.summary?.totalItems ??
          null;
        if (!cancelled && n != null) setWishlistTotalLikes(Number(n));
      } catch (e) {
        console.warn('Analytics wishlist:', e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadProductViewsSummary();
    const id = window.setInterval(loadProductViewsSummary, 60000);
    return () => window.clearInterval(id);
  }, [loadProductViewsSummary]);

  useEffect(() => {
    loadSoldOrders();
  }, [loadSoldOrders]);

  const analyticsData = useMemo(() => {
    const d = dashboardData || {};
    const apiStatusPick = pickAnalyticsStatusCounts(d.statusCounts);
    const ordersFromList = {
      total: orderStatsFromApi.total,
      completed: orderStatsFromApi.completed,
      pending: orderStatsFromApi.pending,
      cancelled: orderStatsFromApi.cancelled,
    };
    const ordersBlock = soldSalesLoading
      ? {
          total: d.totalOrders || 0,
          completed: apiStatusPick.completed,
          pending: apiStatusPick.pending,
          cancelled: apiStatusPick.cancelled,
        }
      : ordersFromList;

    return {
      revenue: {
        total: soldSales.all.grossLines,
        monthly: soldSales.month.grossLines,
        weekly: soldSales.week.grossLines,
        daily: soldSales.today.grossLines,
        apiTotal: d.totalRevenue || 0,
        cod: soldSales.all.codLines,
        nonCod: soldSales.all.nonCodLines,
        soldOrders: soldSales.all.orderCount,
        unitsSold: soldSales.all.unitsSold,
      },
      orders: ordersBlock,
      products: {
        total: d.totalProducts || 0,
        active: d.totalProducts || 0,
        inactive: 0,
        outOfStock: d.lowStockProducts?.length || 0,
      },
      customers: {
        total: d.totalUsers || 0,
        new: Math.round((d.totalUsers || 0) * 0.3),
        returning: Math.round((d.totalUsers || 0) * 0.7),
        active: Math.round((d.totalUsers || 0) * 0.8),
      },
    };
  }, [dashboardData, soldSales, soldSalesLoading, orderStatsFromApi]);

  const orderStatusBreakdown = useMemo(() => {
    if (!soldSalesLoading && orderStatsFromApi.statusCounts) {
      return orderStatsFromApi.statusCounts;
    }
    const sc = dashboardData?.statusCounts;
    if (sc && typeof sc === 'object' && Object.keys(sc).length > 0) {
      return sc;
    }
    return orderStatsFromApi.statusCounts;
  }, [soldSalesLoading, orderStatsFromApi, dashboardData]);

  const handleRefresh = () => {
    refetch();
    loadProductViewsSummary();
    loadSoldOrders();
  };

  if (loading) {
    return (
      <AdminLoading
        title="Loading Analytics"
        subtitle="Fetching analytics data..."
        icon="📈"
      />
    );
  }

  if (error) {
    return (
      <div className="analytics analytics-page">
        <div className="analytics-error-state">
          <div className="analytics-error-state__icon" aria-hidden>
            <i className="fas fa-triangle-exclamation" />
          </div>
          <h3>Could not load analytics</h3>
          <p>{error}</p>
          <button type="button" onClick={handleRefresh} className="analytics-error-state__retry">
            <i className="fas fa-rotate-right" aria-hidden /> Try again
          </button>
        </div>
      </div>
    );
  }

  const orderTotalForBar = analyticsData.orders.total || 1;

  return (
    <div className="analytics analytics-page">
      <div className="analytics-header">
        <div className="header-content">
          <h1>{isTeamMember ? 'Your Analytics' : 'Analytics'}</h1>
          <p className="analytics-subtitle">
            {isTeamMember
              ? 'Insights for your products and orders'
              : 'Store performance, revenue, catalog health, and customer activity'}
          </p>
        </div>
        <div className="header-actions">
          <button type="button" onClick={handleRefresh} className="refresh-btn">
            <i className="fas fa-sync-alt" aria-hidden /> Refresh
          </button>
        </div>
      </div>

      <section className="analytics-page-section" aria-labelledby="analytics-revenue-heading">
        <AnalyticsSectionHead
          icon="fa-chart-line"
          title="Revenue"
          description="Sold payment totals aligned with the Sales report (confirmed orders, excluding pending and cancelled)."
          id="analytics-revenue-heading"
        />
        <div className="analytics-page-grid analytics-page-grid--2">
          <AnalyticsPanel
            variant="revenue"
            icon="fa-sack-dollar"
            title="Total revenue (sold)"
            hero={soldSalesLoading ? null : formatPkrs(analyticsData.revenue.total)}
            heroLabel="All time · line items"
            loading={soldSalesLoading}
          >
            {!soldSalesLoading && (
              <>
                <AnalyticsInfoCallout>
                  Counts match <strong>Sales</strong>: confirmed and beyond, not pending or cancelled.
                </AnalyticsInfoCallout>
                <div className="analytics-metric-grid">
                  <AnalyticsMetricTile label="COD payments" value={formatPkrs(analyticsData.revenue.cod)} />
                  <AnalyticsMetricTile
                    label="Non-COD payments"
                    value={formatPkrs(analyticsData.revenue.nonCod)}
                  />
                  <AnalyticsMetricTile
                    label="Sold orders"
                    value={(analyticsData.revenue.soldOrders ?? 0).toLocaleString()}
                  />
                  <AnalyticsMetricTile
                    label="Units sold"
                    value={(analyticsData.revenue.unitsSold ?? 0).toLocaleString()}
                  />
                  <AnalyticsMetricTile label="Today" value={formatPkrs(analyticsData.revenue.daily)} />
                  <AnalyticsMetricTile label="Last 7 days" value={formatPkrs(analyticsData.revenue.weekly)} />
                  <AnalyticsMetricTile label="This month" value={formatPkrs(analyticsData.revenue.monthly)} />
                  {!isTeamMember && (analyticsData.revenue.apiTotal || 0) > 0 ? (
                    <AnalyticsMetricTile
                      label="API (delivered / shipped)"
                      value={formatPkrs(analyticsData.revenue.apiTotal)}
                      muted
                    />
                  ) : null}
                </div>
              </>
            )}
          </AnalyticsPanel>

          <AnalyticsPanel
            variant="orders"
            icon="fa-cart-shopping"
            title="Orders overview"
            hero={
              soldSalesLoading ? null : analyticsData.orders.total.toLocaleString()
            }
            heroLabel="Total orders (from admin orders API)"
            loading={soldSalesLoading}
          >
            {!soldSalesLoading && (
              <>
                <div className="analytics-metric-grid analytics-metric-grid--3">
                  <AnalyticsMetricTile
                    label="Completed / delivered"
                    value={analyticsData.orders.completed.toLocaleString()}
                  />
                  <AnalyticsMetricTile
                    label="Active / in progress"
                    value={analyticsData.orders.pending.toLocaleString()}
                  />
                  <AnalyticsMetricTile
                    label="Cancelled"
                    value={analyticsData.orders.cancelled.toLocaleString()}
                  />
                </div>
                <AnalyticsInfoCallout>
                  Counts are computed from live <strong>GET /admin/orders</strong> data (same list as Orders
                  management), not legacy analytics status keys.
                </AnalyticsInfoCallout>
              </>
            )}
          </AnalyticsPanel>
        </div>
      </section>

      <section className="analytics-page-section" aria-labelledby="analytics-products-heading">
        <AnalyticsSectionHead
          icon="fa-box-open"
          title="Products & engagement"
          description="Catalog size, wishlist interest, and product detail page views from the storefront."
          id="analytics-products-heading"
        />
        <div className="analytics-page-grid">
          <AnalyticsPanel
            variant="catalog"
            icon="fa-boxes-stacked"
            title="Catalog"
            hero={analyticsData.products.total.toLocaleString()}
            heroLabel="Products in catalog"
          >
            <div className="analytics-metric-grid analytics-metric-grid--3">
              <AnalyticsMetricTile label="Active" value={analyticsData.products.active.toLocaleString()} />
              <AnalyticsMetricTile label="Inactive" value={analyticsData.products.inactive.toLocaleString()} />
              <AnalyticsMetricTile
                label="Low / out of stock alerts"
                value={analyticsData.products.outOfStock.toLocaleString()}
              />
            </div>
          </AnalyticsPanel>

          <AnalyticsPanel variant="wishlist" icon="fa-heart" title="Wishlist">
            <div className="analytics-metric-grid">
              <AnalyticsMetricTile
                label="Catalog products"
                value={analyticsData.products.total.toLocaleString()}
              />
              <AnalyticsMetricTile
                label="Total saves"
                value={wishlistTotalLikes != null ? wishlistTotalLikes.toLocaleString() : '—'}
              />
            </div>
            <AnalyticsInfoCallout>
              Wishlist saves are counted across all customer accounts on the storefront.
            </AnalyticsInfoCallout>
          </AnalyticsPanel>

          <AnalyticsPanel
            variant="views"
            icon="fa-eye"
            title="Product page views"
            hero={productViewsTotal != null ? productViewsTotal.toLocaleString() : '—'}
            heroLabel="Total PDP views"
          >
            {productViewsTop.length > 0 ? (
              <div className="analytics-ranked-list">
                <h4 className="analytics-ranked-list__title">Top products by views</h4>
                <ol className="analytics-ranked-list__items">
                  {productViewsTop.slice(0, 10).map((row, idx) => {
                    const name =
                      row.name ??
                      row.productName ??
                      row.title ??
                      (row.product && row.product.name) ??
                      'Product';
                    const views = row.views ?? row.viewCount ?? row.count ?? 0;
                    const pid = row.productId ?? row._id ?? row.id ?? idx;
                    return (
                      <li key={String(pid)} className="analytics-ranked-list__row">
                        <span className="analytics-ranked-list__rank">{idx + 1}</span>
                        <span className="analytics-ranked-list__name" title={name}>
                          {name}
                        </span>
                        <span className="analytics-ranked-list__metric">
                          {Number(views).toLocaleString()}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ) : (
              <AnalyticsInfoCallout>No view breakdown yet — data appears as customers browse products.</AnalyticsInfoCallout>
            )}
          </AnalyticsPanel>

          {!isTeamMember && (
            <AnalyticsPanel
              variant="customers"
              icon="fa-users"
              title="Customers"
              hero={analyticsData.customers.total.toLocaleString()}
              heroLabel="Registered customers"
            >
              <div className="analytics-metric-grid analytics-metric-grid--3">
                <AnalyticsMetricTile label="New (est.)" value={analyticsData.customers.new.toLocaleString()} />
                <AnalyticsMetricTile
                  label="Returning (est.)"
                  value={analyticsData.customers.returning.toLocaleString()}
                />
                <AnalyticsMetricTile label="Active (est.)" value={analyticsData.customers.active.toLocaleString()} />
              </div>
              <AnalyticsInfoCallout>
                New, returning, and active splits are approximate until dedicated customer analytics are wired on the API.
              </AnalyticsInfoCallout>
            </AnalyticsPanel>
          )}
        </div>
      </section>

      {orderStatusBreakdown && Object.keys(orderStatusBreakdown).length > 0 && (
        <section className="analytics-page-section" aria-labelledby="analytics-status-heading">
          <AnalyticsSectionHead
            icon="fa-list-check"
            title="Order status breakdown"
            description="Share of orders by current fulfillment status."
            id="analytics-status-heading"
          />
          <div className="analytics-status-board">
            {Object.entries(orderStatusBreakdown)
              .filter(([, count]) => typeof count === 'number' || typeof count === 'string')
              .map(([status, count]) => {
                const n = Number(count) || 0;
                const pct = Math.min(100, (n / orderTotalForBar) * 100);
                const label = status;
                return (
                  <div key={status} className="analytics-status-row">
                    <div className="analytics-status-row__head">
                      <span className="analytics-status-row__name">{label}</span>
                      <span className="analytics-status-row__count">{n.toLocaleString()}</span>
                    </div>
                    <div className="analytics-status-row__track" role="presentation">
                      <div
                        className="analytics-status-row__fill"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: getStatusColor(status),
                        }}
                      />
                    </div>
                    <span className="analytics-status-row__pct">{pct.toFixed(pct >= 10 ? 0 : 1)}%</span>
                  </div>
                );
              })}
          </div>
        </section>
      )}
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('cancel')) return '#dc3545';
  if (s.includes('deliver')) return '#28a745';
  if (s.includes('placed') || s === 'pending') return '#ffc107';
  if (s.includes('process') || s.includes('confirm') || s.includes('pack')) return '#17a2b8';
  if (
    s.includes('dispatch') ||
    s.includes('transit') ||
    s.includes('delivery') ||
    s.includes('ship')
  ) {
    return '#6f42c1';
  }
  const colors = {
    pending: '#ffc107',
    processing: '#17a2b8',
    shipped: '#6f42c1',
    delivered: '#28a745',
    cancelled: '#dc3545',
  };
  return colors[s] || '#6c757d';
};

export default Analytics;
